/* eslint-disable @typescript-eslint/no-explicit-any */
// React and hooks
import React, { CSSProperties, useEffect, useRef, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'

// Third-party libraries
import moment from 'moment'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { Internationalization } from '@syncfusion/ej2-base'
import { Tooltip } from '@syncfusion/ej2-popups'

// Custom components
import Button from '@components/common/Button/Button.component'
import './MoveAppointmentModal.css'

// Redux actions and state management
import {
  deleteAppointment,
  updateAppointment,
} from '@store/actions/appointment.action'
import { AppDispatch } from '@store/index' // Import your AppDispatch type
import { useNotificationToast } from '@context/NotificationToastContext'
import { createMovedAppointmentToast } from '@utils/notificationToast.util'

// Types and interfaces
import {
  AppointmentRequest,
  AppointmentResponse,
  AppointmentStatus,
  IOperatory,
  IProvider,
} from '@dataTypes/appointment.type'
import { STATUS_STYLES } from '@components/common/StatusBadge/StatusBadge.component'
import {
  EventHeaderIcon,
  StatusData,
} from '../EventHeaderIcon/EventHeaderIcon.component'
import { formatDateTime } from '@utils/time.util'
import { maskString } from '@utils/mask-string'
import { Provider } from '@dataTypes/provider.types'
import {
  getProviderBorderColor,
  getProviderTextColor,
} from '@constants/Providers'
import { SchedulerSettingsType } from '@components/pages/Settings/System/Scheduler/Scheduler.component'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { AppointmentStatusMap } from '@utils/appointment.util'
import { aiReceptionistActorHint } from '@utils/aiReceptionist.util'
import { Icon as AiReceptionistIcon } from '@enamelai/design-system/icons/ai_receptionist'
const instance = new Internationalization()

export const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const [firstName, ...lastNameParts] = fullName.split(' ')
  const lastName = lastNameParts.join(' ') // Handle cases with multiple last names
  return { firstName, lastName }
}

export const CustomResourceHeader = (props: any): React.ReactElement => {
  const { name } = props.resourceData

  return (
    <>
      <Row>
        <Col md={12}>
          <Row>
            <TooltipComponent
              content={name}
              cssClass="e-tooltip-template-css e-tooltip-tab-feature"
            >
              <Col md={12} className="custom-header-title">
                {`${name}`}
              </Col>
            </TooltipComponent>
          </Row>
        </Col>
      </Row>
    </>
  )
}

export const majorSlotTemplate = (props: any): React.ReactElement => {
  const formattedTime = instance
    .formatDate(props.date, { skeleton: 'Hm' })
    .replace(' AM', '')
    .replace(' PM', '')

  return <div className="time-scale">{formattedTime}</div>
}

export const minorSlotTemplate = (props: any): React.ReactElement => {
  const formattedTime = instance
    .formatDate(props.date, { skeleton: 'Hm' })
    .replace(' AM', '')
    .replace(' PM', '')

  return (
    <div className="time-scale-half-hour" style={{ textAlign: 'center' }}>
      {formattedTime}
    </div>
  )
}

export const handleDelete = (
  apptId: string,
  dispatch: AppDispatch,
  closeDeleteModal: () => void,
): void => {
  dispatch(deleteAppointment(apptId))
  closeDeleteModal()
}

export const getCancelScheduleText = (
  apptId: string,
  appointmentList: AppointmentResponse[],
): { title: string; description: string } => {
  const appointment = appointmentList.find(
    (appt) => appt.appointment_id === apptId,
  )
  const name = `${appointment?.patient?.first_name ?? ''} ${
    appointment?.patient?.last_name ?? ''
  }`.trim()
  const typeName = appointment?.appointmenttype?.name?.trim() ?? ''
  const dateLabel = appointment?.appt_date
    ? moment(appointment.appt_date).format('MMM DD, YYYY')
    : ''
  const timeLabel = appointment?.appt_date
    ? moment(appointment.appt_date)
        .add(appointment?.appt_start)
        .format('hh:mm A')
    : ''

  const subject = [name ? `${name}'s` : 'this', typeName, 'appointment']
    .filter(Boolean)
    .join(' ')
  const when = [dateLabel && `on ${dateLabel}`, timeLabel && `at ${timeLabel}`]
    .filter(Boolean)
    .join(' ')

  return {
    title: 'Delete appointment?',
    description: `Are you sure you want to delete ${[subject, when]
      .filter(Boolean)
      .join(' ')}? This action can't be undone.`,
  }
}

export const deleteModalBody = (
  apptId: string,
  appointmentList: AppointmentResponse[],
): JSX.Element => {
  const { title, description } = getCancelScheduleText(apptId, appointmentList)

  return (
    <div className="confirmation-modal__body">
      <p className="confirmation-modal__title">{title}</p>
      <p className="confirmation-modal__message">{description}</p>
    </div>
  )
}

export const deleteModalFooter = (
  apptId: string,
  dispatch: AppDispatch,
  closeDeleteModal: () => void,
  onAccept?: () => void,
): JSX.Element => {
  return (
    <div className="confirmation-modal__footer">
      <Button className="button-secondary" size="md" onClick={closeDeleteModal}>
        Cancel
      </Button>
      <Button
        className="button-destructive-primary"
        size="md"
        onClick={() =>
          onAccept
            ? onAccept()
            : handleDelete(apptId, dispatch, closeDeleteModal)
        }
      >
        Delete
      </Button>
    </div>
  )
}

// Drag and drop modal confirmation

const handleConfirm = (
  type: 'Switch Provider' | 'Move with provider',
  data: AppointmentResponse,
  providerId: string | null,
  dispatch: AppDispatch,
  closeDeleteModal: () => void,
  providerList: Provider[],
): Promise<unknown> => {
  if (!data) return Promise.resolve()

  let updatedAppt: Partial<AppointmentRequest> = {
    appt_start: formatDateTime(`${data.appointment_start_date}`, 'HH:mm'),
    appt_end: formatDateTime(`${data.appointment_end_date}`, 'HH:mm'),
    appt_date: formatDateTime(`${data.appointment_start_date}`, 'YYYY-MM-DD'),
    operatory_id: data?.operatory_id || '',
  }

  // Handle switching provider logic
  if (type === 'Switch Provider' && providerId) {
    const providerInfo = providerList.find(
      (doctor) => doctor.provider_id === providerId,
    )

    if (providerInfo) {
      updatedAppt = {
        ...updatedAppt,
        provider_id: providerInfo.provider_id,
      }
    }
  }

  return dispatch(
    updateAppointment({
      appointment_id: data.appointment_id,
      updates: updatedAppt,
      updateByDragAndDrop: true,
    }),
  )
    .unwrap()
    .finally(() => {
      setTimeout(() => {
        closeDeleteModal()
      }, 100)
    })
}
export type MoveAppointmentModalProps = {
  show: boolean
  onHide: () => void
  onExited?: () => void
  data: AppointmentResponse
  previousOperatoryId?: string | null
  dispatch: AppDispatch
  providerList: Provider[]
  availableProviderList: any[]
  operatoryList?: IOperatory[]
  onViewDetails?: (appointment: AppointmentResponse) => void
}

const getOperatoryName = (
  operatoryList: IOperatory[] | undefined,
  operatoryId?: string | null,
): string => {
  if (!operatoryId) return 'Unknown room'
  return (
    operatoryList?.find((room) => room.operatory_id === operatoryId)?.name ??
    'Unknown room'
  )
}

const getProviderDisplayName = (provider?: IProvider | null): string => {
  if (!provider?.first_name && !provider?.last_name) return 'Unknown provider'
  return `Dr. ${provider.first_name ?? ''} ${provider.last_name ?? ''}`.trim()
}

type MoveAppointmentProviderOption = Pick<
  IProvider,
  'provider_id' | 'first_name' | 'last_name'
>

export const canConfirmMoveAppointment = (
  availableProviderList: { provider_id?: string | null }[],
  currentProviderId?: string | null,
): boolean => {
  const currentProviderAvailable = availableProviderList.some(
    (provider) => provider.provider_id === currentProviderId,
  )
  const otherProviderAvailable = availableProviderList.some(
    (provider) =>
      Boolean(provider.provider_id) &&
      provider.provider_id !== currentProviderId,
  )
  return currentProviderAvailable || otherProviderAvailable
}

export const MoveAppointmentModal: React.FC<MoveAppointmentModalProps> = ({
  show,
  onHide,
  onExited,
  data,
  previousOperatoryId,
  dispatch,
  providerList,
  availableProviderList,
  operatoryList,
  onViewDetails,
}) => {
  const { showToast } = useNotificationToast()
  const [selectedProvider, setSelectedProvider] = useState<string>('')

  useEffect(() => {
    if (!show) {
      setSelectedProvider('')
    }
  }, [show])

  const sourceRoomName = getOperatoryName(operatoryList, previousOperatoryId)
  const targetRoomName = getOperatoryName(operatoryList, data.operatory_id)
  const currentProviderName = getProviderDisplayName(data.provider)
  const currentProviderId = data.provider_id
  const isCurrentProviderAvailable = availableProviderList.some(
    (provider) => provider.provider_id === currentProviderId,
  )
  const providerOptions: MoveAppointmentProviderOption[] = availableProviderList
    .filter((provider) => provider.provider_id !== currentProviderId)
    .map((provider) => ({
      provider_id: provider.provider_id,
      first_name: provider.first_name,
      last_name: provider.last_name,
    }))

  const handleActionConfirm = (
    type: 'Switch Provider' | 'Move with provider',
  ): void => {
    void handleConfirm(
      type,
      data,
      selectedProvider || null,
      dispatch,
      onHide,
      providerList,
    )
      .then(() => {
        showToast(
          createMovedAppointmentToast(
            sourceRoomName,
            targetRoomName,
            onViewDetails ? () => onViewDetails(data) : undefined,
          ),
        )
      })
      .catch(() => undefined)
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      onExited={onExited}
      centered
      className="move-appointment-modal"
      dialogClassName="move-appointment-modal-dialog"
      contentClassName="move-appointment-modal-content"
    >
      <Modal.Header closeButton className="move-appointment-modal-header">
        <Modal.Title>Move appointment</Modal.Title>
      </Modal.Header>

      <Modal.Body className="move-appointment-modal-body">
        <div className="move-appointment-modal-summary">
          <div className="move-appointment-modal-heading">
            <span>Move appointment:</span>
            <span>
              {sourceRoomName} → {targetRoomName}
            </span>
          </div>
          <p className="move-appointment-modal-hint">
            Select a new provider if you want to switch to another one.
          </p>
          <p className="move-appointment-modal-current-provider">
            Current provider: {currentProviderName}
          </p>
        </div>

        <Form.Group className="move-appointment-modal-provider-field">
          <Form.Label>Switch to another provider</Form.Label>
          <Form.Select
            value={selectedProvider}
            onChange={(event) => setSelectedProvider(event.target.value)}
          >
            <option value="">Select provider</option>
            {providerOptions.map((provider) => (
              <option key={provider.provider_id} value={provider.provider_id}>
                {`${provider.first_name} ${provider.last_name}`}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="move-appointment-modal-footer">
        <div className="move-appointment-modal-footer-left">
          <Button
            className="button-secondary move-appointment-modal-cancel"
            size="sm"
            onClick={onHide}
          >
            Cancel move
          </Button>
        </div>
        <div className="move-appointment-modal-footer-right">
          <Button
            className="move-appointment-modal-new-provider"
            size="sm"
            disabled={!selectedProvider}
            onClick={() => handleActionConfirm('Switch Provider')}
          >
            Move with new provider
          </Button>
          <Button
            className="button-primary"
            size="sm"
            onClick={() => handleActionConfirm('Move with provider')}
            disabled={!isCurrentProviderAvailable}
          >
            Move with current provider
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export type DragAndDropModalFooterProps = {
  data: any
  dispatch: AppDispatch
  closeDeleteModal: () => void
  providerList: Provider[]
  operatoryList?: IOperatory[]
}

/** @deprecated Use MoveAppointmentModal instead */
export const DragAndDropModalBody: React.FC<{
  data: any
  operatoryList?: IOperatory[]
}> = () => null

/** @deprecated Use MoveAppointmentModal instead */
export const DragAndDropModalFooter: React.FC<
  DragAndDropModalFooterProps
> = () => null

const previewColumn: { field: keyof AppointmentResponse; title: string }[] = [
  {
    field: 'patient',
    title: 'Patient name:',
  },
  {
    field: 'provider',
    title: 'Provider:',
  },
  // {
  //   field: 'clinic_id',
  //   title: 'Clinic:',
  // },
  {
    field: 'appointment_type_id',
    title: 'Procedure:',
  },
  {
    field: 'appt_date',
    title: 'Date & Time:',
  },
  {
    field: 'appt_start',
    title: '',
  },
  {
    field: 'appt_status',
    title: 'Status:',
  },
  {
    field: 'patient',
    title: 'Pref. Dentist:',
  },
  {
    field: 'patient',
    title: 'Pref. Hygienist:',
  },
  {
    field: 'is_insured',
    title: 'Is Insured:',
  },
  // {
  //   field: 'notes',
  //   title: 'Note:',
  // },
]

const displayTextString = (
  props: Partial<AppointmentResponse>,
  field: keyof AppointmentResponse,
  isHipaa: boolean,
  title: string,
): string => {
  // Helper to convert a style object to inline CSS string
  const styleObjectToString = (styleObj: CSSProperties): string =>
    Object.entries(styleObj)
      .map(
        ([key, val]) =>
          `${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}:${val}`,
      )
      .join(';')

  // Patient name
  if (field === 'patient' && title === 'Patient name:') {
    return isHipaa
      ? `${maskString(props.patient?.first_name)} ${maskString(
          props.patient?.last_name,
        )}`
      : `${props.patient?.first_name ?? ''} ${props.patient?.last_name ?? ''}`
  }

  // Preferred dentist
  if (field === 'patient' && title === 'Pref. Dentist:') {
    return props.patient?.preferredProvider
      ? `Dr. ${props.patient.preferredProvider.first_name} ${props.patient.preferredProvider.last_name}`
      : 'No'
  }

  // Preferred hygienist
  if (field === 'patient' && title === 'Pref. Hygienist:') {
    return props.patient?.preferredHygienist
      ? `Dr. ${props.patient.preferredHygienist.first_name} ${props.patient.preferredHygienist.last_name}`
      : 'No'
  }

  // Provider
  if (field === 'provider' || field === 'provider_id') {
    return `Dr. ${props.provider?.first_name ?? ''} ${props.provider?.last_name ?? ''}`
  }

  // Appointment date
  if (field === 'appt_date') {
    return props[field] ? moment(props[field]).format('MMM DD, YYYY') : ''
  }

  // Appointment start time and end time
  if (field === 'appt_start') {
    return props[field]
      ? `${moment(props[field], 'HH:mm').format('hh:mm A')}${props.appt_end ? ` - ${moment(props.appt_end, 'HH:mm').format('hh:mm A')}` : ''}`
      : ''
  }

  // Appointment status
  if (field === 'appt_status') {
    const value = props[field] || 0
    if (typeof value === 'number') {
      const mappedStatus = AppointmentStatusMap[value as AppointmentStatus]
      if (mappedStatus) {
        // Use STATUS_STYLES from your React component
        const badgeStyle = STATUS_STYLES[mappedStatus] || {
          backgroundColor: '#E0E0E0',
          color: '#ffffff',
        }
        const styleString = styleObjectToString(badgeStyle)
        return `<span class="badge-status" style="${styleString}" data-status="${mappedStatus}">${mappedStatus}</span>`
      }
    }
    return '-'
  }

  // Insurance
  if (field === 'is_insured') {
    return props[field] ? 'Yes (Verified)' : 'No'
  }

  // Files
  if (field === 'files') {
    return props[field]?.map((file) => file.name).join(', ') ?? ''
  }

  // Appointment type (procedure codes)
  if (field === 'appointment_type_id') {
    return Array.from(
      new Set(props.procedure_logs?.map((proc) => proc.proc_code) ?? []),
    ).join(', ')
  }

  // Fields to skip
  if (
    field === 'procedure_logs' ||
    field === 'contact_via' ||
    field === 'template' ||
    field === 'insurance' ||
    field === 'appointmenttype' ||
    field === 'notes' ||
    field === 'lab_orders' ||
    field === 'patient'
  ) {
    return ''
  }

  // Fallback for other primitive values
  const fallback = props[field]
  if (
    typeof fallback === 'string' ||
    typeof fallback === 'number' ||
    typeof fallback === 'boolean'
  ) {
    return String(fallback ?? '')
  }

  return ''
}

export const tooltipTemplateString = (props: any, isHipaa: boolean): string => {
  const rows = previewColumn
    .map(({ field, title }) => {
      const value = displayTextString(props, field, isHipaa, title) || '-'

      return `
        <tr>
          <th>${title}</th>
          <td>${value}</td>
        </tr>
      `
    })
    .join('')

  return `
    <div class="appt-tooltip-wrapper">
      <div class="appt-tooltip-header">Appointment Info</div>
      <div class="border"></div>
      <div>
        <table class="appt-tooltip">
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    </div>
  `
}

// small helper to cut down on boilerplate
const el = (
  tag: string,
  options: {
    className?: string
    style?: Partial<CSSStyleDeclaration> & Record<string, any>
    text?: string
    html?: string
    title?: string
  } = {},
  children: (Node | null | undefined)[] = [],
): HTMLElement => {
  const node = document.createElement(tag)
  if (options.className) node.className = options.className
  if (options.style) Object.assign(node.style, options.style)
  if (options.text != null) node.textContent = options.text
  if (options.html != null) node.innerHTML = options.html
  if (options.title) node.title = options.title
  children.forEach((c) => c && node.appendChild(c))
  return node
}

export const createEventAiIcon = (
  hint: string,
  diamondColor: string,
): HTMLElement => {
  const node = document.createElement('span')
  node.className = 'event-ai-icon'
  node.title = hint
  node.style.setProperty('--event-ai-diamond', diamondColor)
  node.innerHTML = renderToStaticMarkup(
    <AiReceptionistIcon aria-hidden="true" focusable="false" />,
  )
  return node
}

type AiIconAppointment = {
  appointment_id?: string
  created_by?: string | null
  updated_by?: string | null
  provider?: { provider_color?: { header?: string | null } | null } | null
}

/** Inserts the tooth on cards already painted, without rebuilding the schedule. */
export const paintAiReceptionistIcons = (
  root: ParentNode,
  appointments: AiIconAppointment[],
  readEvent: (element: Element) => AiIconAppointment | null | undefined,
  aiUserId?: string | null,
): void => {
  if (!aiUserId) return

  const byId = new Map<string, AiIconAppointment>()
  appointments.forEach((appointment) => {
    if (appointment.appointment_id) {
      byId.set(appointment.appointment_id, appointment)
    }
  })

  root
    .querySelectorAll('.e-appointment:not(.e-block-appointment)')
    .forEach((node) => {
      if (node.querySelector('.event-ai-icon')) return
      const timeWrap = node.querySelector('.event-text-sub.text-white')
      if (!timeWrap) return

      const details = readEvent(node) ?? {}
      const source =
        (details.appointment_id && byId.get(details.appointment_id)) || details
      const hint = aiReceptionistActorHint(source, aiUserId, 'appointment')
      if (!hint) return

      const diamond =
        source.provider?.provider_color?.header ||
        details.provider?.provider_color?.header ||
        '#ffffff'
      timeWrap.appendChild(createEventAiIcon(hint, diamond))
    })
}

/** Reads the actor id at event-render time and patches cards painted earlier. */
export const usePaintAppointmentAiIcons = (
  containerRef: React.RefObject<HTMLElement | null>,
  scheduleRef: React.RefObject<{
    getEventDetails?: (element: any) => AiIconAppointment | undefined
  } | null>,
  appointments: AiIconAppointment[],
  aiUserId: string | null,
): React.MutableRefObject<string | null> => {
  const aiUserIdRef = useRef(aiUserId)
  aiUserIdRef.current = aiUserId

  useEffect(() => {
    if (!aiUserId) return
    let cancelled = false
    let timeoutId = 0
    const rafId = requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => {
        if (cancelled) return
        const root = containerRef.current
        if (!root) return
        paintAiReceptionistIcons(
          root,
          appointments,
          (element) => scheduleRef.current?.getEventDetails?.(element),
          aiUserId,
        )
      }, 0)
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      window.clearTimeout(timeoutId)
    }
  }, [aiUserId, appointments, containerRef, scheduleRef])

  return aiUserIdRef
}

export const CustomEventTemplatePlainDOM = (
  props: any,
  schedulerSettings: SchedulerSettingsType,
  isHipaa = false,
  aiUserId?: string | null,
): HTMLElement => {
  const statusData: StatusData = {
    appointment_status: props.appt_status,
    patient_status: props.patient_status,
    form_status: props.form_status,
    payment_status: props.payment_status,
    other_status: props.other_status || 0,
  }
  const provider = props.provider
  const providerColor = provider?.provider_color
  const providerTextColor = providerColor?.text_color ?? ''
  const providerInfoVisible =
    schedulerSettings.show_provider_information &&
    provider?.first_name &&
    provider?.last_name
  const patientVisible = props.patient?.first_name && props.patient?.last_name
  const appointmentTypeVisible = schedulerSettings.show_appointment_type
  const slotsPerHour = 60 / schedulerSettings.time_slot_interval
  const slotMinutes = 60 / slotsPerHour

  const rows = [
    {
      name: 'patient',
      visible: patientVisible,
      className: 'event-text-sub',
      style: {
        fontSize: '12px',
        fontWeight: '500',
        color: getProviderTextColor(providerColor?.label),
        ...(!schedulerSettings.show_appointment_duration &&
        !schedulerSettings.status_indicator
          ? { marginTop: '-4px' }
          : {}),
      } as Record<string, any>,
      content: isHipaa
        ? `${maskString(props.patient?.first_name ?? '')} ${maskString(props.patient?.last_name ?? '')}`
        : `${props.patient?.first_name ?? ''} ${props.patient?.last_name ?? ''}`,
    },
    {
      name: 'appointment-type',
      visible: appointmentTypeVisible,
      className: 'event-text-sub',
      style: { color: providerTextColor } as Record<string, any>,
      content: props.appointmenttype?.name,
    },
    {
      name: 'provider',
      visible: providerInfoVisible,
      className: 'event-text-sub',
      style: {
        color: providerTextColor,
        fontSize: '11px',
        opacity: '0.9',
      } as Record<string, any>,
      content: `Dr. ${provider?.first_name ?? ''} ${provider?.last_name ?? ''}`,
    },
  ]

  const aiHint = aiReceptionistActorHint(props, aiUserId, 'appointment')
  const showHeader =
    schedulerSettings.show_appointment_duration ||
    schedulerSettings.status_indicator ||
    Boolean(aiHint)

  const colChildren: (Node | null)[] = []
  const timeWrap = el('div', { className: 'event-text-sub text-white' })
  if (schedulerSettings.show_appointment_duration) {
    timeWrap.appendChild(
      el('span', {
        text: `${moment(props.appointment_start_date).format('hh:mm A')} - ${moment(
          props.appointment_end_date,
        ).format('hh:mm A')}`,
      }),
    )
  }
  if (aiHint) {
    timeWrap.appendChild(
      createEventAiIcon(aiHint, providerColor?.header || '#ffffff'),
    )
  }
  colChildren.push(timeWrap)

  const iconWrap = el('div')
  if (schedulerSettings.status_indicator) {
    const iconSpan = el('span', {
      className: 'event-header-icon',
      html: renderToStaticMarkup(
        <EventHeaderIcon
          statusData={
            {
              appointment_status: statusData.appointment_status,
            } as StatusData
          }
        />,
      ),
    })
    iconWrap.appendChild(iconSpan)
  }
  colChildren.push(iconWrap)

  const col = el(
    'div',
    {
      className: 'col-md-12 event-header',
      style: {
        backgroundColor: providerColor?.header ?? '',
        height: '20px',
        marginRight: '4px',
      },
    },
    colChildren,
  )

  const header = el(
    'div',
    { style: { display: showHeader ? 'flex' : 'none', alignItems: 'center' } },
    [col],
  )

  let body: HTMLElement
  if (!showHeader && props.duration < slotMinutes) {
    body = el('div', { style: { height: '20px' } }, [])
  } else {
    body = el('div', {
      className: 'event-body',
      style: {
        ...(props.duration === slotMinutes * 2 ? { paddingTop: '0px' } : {}),
      },
    })
    rows.forEach((row) => {
      const rowEl = el(
        'div',
        {
          className: `event-row ${row.name}`,
          style: { width: '100%', display: row.visible ? 'block' : 'none' },
        },
        [
          el('span', {
            className: row.className,
            style: row.style,
            text: row.content,
          }),
        ],
      )
      body.appendChild(rowEl)
    })
  }

  const container = el(
    'div',
    {
      className: 'appointment-event-container',
      style: {
        position: 'relative',
        height: '100%',
        minHeight: '1.75px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: 'pointer',
      },
    },
    [header, body],
  )

  const tooltip = new Tooltip({
    content: tooltipTemplateString(props, isHipaa),
    opensOn: 'Hover',
    beforeOpen: (e: any) => {
      const existingTooltips = document.querySelectorAll(
        '.e-tooltip-wrap.e-popup-open:has(.appt-tooltip-wrapper)',
      ) as unknown as HTMLElement[]
      existingTooltips.forEach((t) => {
        t.style.display = 'none'
      })
      const scheduleContextMenu = document.querySelector(
        '.schedule-context-menu #overviewContextMenu',
      ) as HTMLElement | null
      if (
        scheduleContextMenu &&
        scheduleContextMenu.style.display !== 'none' &&
        scheduleContextMenu.style.display !== ''
      ) {
        e.cancel = true
      }
    },
  })
  tooltip.appendTo(container)

  return container
}

export const handleEventRendered = (
  args: any,
  schedulerSettings: SchedulerSettingsType,
  aiUserId?: string | null,
): void => {
  const eventElement = args.element as HTMLElement

  const customEl = CustomEventTemplatePlainDOM(
    args.data,
    schedulerSettings,
    false,
    aiUserId,
  )

  eventElement?.querySelector('.e-appointment-details')?.appendChild(customEl)

  const appointment = args.data as AppointmentResponse
  if (!args.element) return

  if (appointment.duration === 15) {
    args.element.style.padding = '1px'
    args.element.style.borderRadius = '4px'
  }

  args.element.style.backgroundColor =
    appointment.provider?.provider_color?.background || '#ffffff'

  const borderColor = getProviderBorderColor(
    appointment.provider?.provider_color?.label,
  )
  args.element.style.border = `1px solid ${borderColor}`

  setTimeout(() => {
    const appointmentEventContainer = args?.element?.querySelector(
      '.appointment-event-container',
    )
    if (appointmentEventContainer) {
      appointmentEventContainer.style.height =
        args.element.offsetHeight - 10 + 'px'
    }
  }, 10)
}

type ScheduleResult = {
  scheduler_hour_from: string
  scheduler_hour_to: string
  opening_from: string | null
  opening_to: string | null
}

export type DateScheduleResult = {
  date: string
  schedule: ScheduleResult
}

function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00:00`
}

function timeToSeconds(time: string): number {
  const [hour, minute, second] = time.split(':').map(Number)

  return hour * 3600 + minute * 60 + (second || 0)
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

function getScheduleForSingleDate(
  date: string,
  schedulerSettings: any,
  openingHours: any[],
  holidays: any[],
  overrides: any[],
): ScheduleResult {
  const dayName = moment(date).format('dddd')

  let openingFrom: string | null = null
  let openingTo: string | null = null

  const override = overrides.find((item) => item.override_date === date)

  if (override) {
    openingFrom = override.start_time
    openingTo = override.end_time
  }

  if (!override) {
    const holiday = holidays.find((item) => item.holiday_date === date)

    if (holiday) {
      openingFrom = holiday.start_time
      openingTo = holiday.end_time
    }
  }

  if (!override && !openingFrom) {
    const opening = openingHours.find((item) => item.day_of_week === dayName)

    if (opening && opening.is_open) {
      openingFrom = opening.start_time
      openingTo = opening.end_time
    }
  }

  let schedulerFrom = formatHour(schedulerSettings.working_hours_from)
  let schedulerTo = formatHour(schedulerSettings.working_hours_to)

  if (openingFrom && openingTo) {
    if (timeToSeconds(openingFrom) < timeToSeconds(schedulerFrom)) {
      schedulerFrom = openingFrom
    }

    if (timeToSeconds(openingTo) > timeToSeconds(schedulerTo)) {
      schedulerTo = openingTo
    }
  }

  return {
    scheduler_hour_from: schedulerFrom,
    scheduler_hour_to: schedulerTo,
    opening_from: openingFrom,
    opening_to: openingTo,
  }
}

export function getScheduleForDateRange(
  startDate: string,
  endDate: string,
  schedulerSettings: any,
  openingHours: any[],
  holidays: any[],
  overrides: any[],
): DateScheduleResult[] {
  const results: DateScheduleResult[] = []

  const currentDate = new Date(startDate)
  const lastDate = new Date(endDate)

  while (currentDate <= lastDate) {
    const date = formatDate(currentDate)

    results.push({
      date,
      schedule: getScheduleForSingleDate(
        date,
        schedulerSettings,
        openingHours,
        holidays,
        overrides,
      ),
    })

    currentDate.setDate(currentDate.getDate() + 1)
  }

  let largestSchedulerFrom = results[0].schedule.scheduler_hour_from
  let largestSchedulerTo = results[0].schedule.scheduler_hour_to

  for (const item of results) {
    if (
      timeToSeconds(item.schedule.scheduler_hour_from) <
      timeToSeconds(largestSchedulerFrom)
    ) {
      largestSchedulerFrom = item.schedule.scheduler_hour_from
    }

    if (
      timeToSeconds(item.schedule.scheduler_hour_to) >
      timeToSeconds(largestSchedulerTo)
    ) {
      largestSchedulerTo = item.schedule.scheduler_hour_to
    }
  }

  return results.map((item) => ({
    ...item,
    schedule: {
      ...item.schedule,
      scheduler_hour_from: largestSchedulerFrom,
      scheduler_hour_to: largestSchedulerTo,
    },
  }))
}
